import { FC, useState, ChangeEvent, FormEvent } from "react";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { validateMobile, validateUsername } from "../../utils/validationUtils";
import { TextArea } from "../ui/TextArea";
import { request } from "../../services/authService";
import { useStation } from "../../contexts/StationContext";
import { Modal } from "../ui/Modal";

type RequestFormProps = {
    isOpen: boolean;
    type: 'studio_visit' | 'lets_collab';
    rj?: { profileId: string, profileName: string };
    onClose: () => void;
}

export const RequestForm: FC<RequestFormProps> = ({ isOpen, type, rj, onClose }) => {
    const { stations } = useStation();
    const initialFormState = { name: { value: '', error: '' }, mobile: { value: '', error: '' }, station: { value: '', error: '' }, reason: { value: '', error: '' } };
    const [result, setResult] = useState<{ success: boolean, message: string }>({ success: false, message: '' });

    const [form, setForm] = useState<{ [key: string]: { id?: string, value: string, error: string } }>({ name: { value: '', error: '' }, mobile: { value: '', error: '' }, station: { value: '', error: '' }, reason: { value: '', error: '' } });

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string, id: string, value: string }) => {
        if ('target' in e) {
            setForm(prev => ({ ...prev, [e.target.name]: { ...prev[e.target.name], value: e.target.value, error: '' } }));
        } else {
            setForm(prev => ({ ...prev, [e.name]: { ...prev[e.name], id: e.id, value: e.value, error: '' } }));
        }
    }

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (validateUsername(form.name.value)) {
            setForm(prev => ({ ...prev, name: { ...prev.name, error: validateUsername(form.name.value) } }));
        } else if (validateMobile(form.mobile.value)) {
            setForm(prev => ({ ...prev, mobile: { ...prev.mobile, error: validateMobile(form.mobile.value) } }));
        } else if (!form.station.value.trim()) {
            setForm(prev => ({ ...prev, station: { ...prev.station, error: 'Station is required' } }));
        } else if (!form.reason.value.trim()) {
            setForm(prev => ({ ...prev, reason: { ...prev.reason, error: 'Reason is required' } }));
        } else {
            const requestBody = {
                requestType: type,
                name: form.name.value.trim(),
                contactNumber: form.mobile.value,
                stationDetail: { stationId: form.station.id || '', stationName: form.station.value },
                reason: form.reason.value.trim(),
                ...(type === 'lets_collab' && rj && { rjDetail: { profileId: rj?.profileId, profileName: rj?.profileName } })
            }
            request(requestBody).then(response => {
                setResult({ success: true, message: 'Request submitted successfully' });
                setForm(initialFormState);
                // onClose();
            }).catch(error => {
                console.log(error);
                setResult({ success: false, message: 'Something went wrong! Please try again later.' });
            });
        }
    };
    const onCloseButtonClick = () => {
        setResult({ success: false, message: '' });
        setForm(initialFormState);

        onClose();
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            {result.success ? (
                <div className="flex flex-col overflow-y-auto -mr-61 pr-61 max-h-screen">   
                    <div className={`sm:w-[400px] lg:h-44 p-4 pt-8 sm:p-8 flex flex-col gap-2 justify-center items-center ${type === 'studio_visit' ? "bg-[url('assets/images/studio-visit-banner.png')]" : "bg-[url('assets/images/lets-collab-banner.png')]"} bg-cover bg-center`}>
                        <h3 className="text-white text-center text-lg md:text-2xl font-bold">{type === 'studio_visit' ? 'Visit the Studio & Hang Out with Your Favorite RJ!' : 'Let’s Collab with Your Favorite RJ!'}</h3>
                       
                    </div>
                    <div className="flex flex-col gap-4 px-4 md:px-6 pb-4 md:pb-6 bg-black">  
                        <p className="my-8 text-white text-center text-sm md:text-base font-normal">
                            {result.message}
                        </p>
                    <Button variant="primary" onClick={onCloseButtonClick}>Close</Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col overflow-y-auto -mr-61 pr-61 max-h-screen">
                <div className={`sm:w-[400px] lg:h-44 p-4 pt-8 sm:p-8 flex flex-col gap-2 justify-center items-center ${type === 'studio_visit' ? "bg-[url('assets/images/studio-visit-banner.png')]" : "bg-[url('assets/images/lets-collab-banner.png')]"} bg-cover bg-center`}>
                    <h3 className="text-white text-center text-lg md:text-2xl font-bold">{type === 'studio_visit' ? 'Visit the Studio & Hang Out with Your Favorite RJ!' : 'Let’s Collab with Your Favorite RJ!'}</h3>
                    <p className="text-white text-center text-sm md:text-base font-normal">Please submit your details</p>
                </div>
                <form className="flex flex-col gap-4 px-4 md:px-6 pb-4 md:pb-6 bg-black" noValidate onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-4 overflow-y-auto max-h-[calc(100vh-250px)] mt-2 pr-4 -mr-4">
                        <Input
                            variant="secondary"
                            type="text"
                            name="name"
                            label="Name"
                            placeholder="Enter your name"
                            minLength={3}
                            maxLength={20}
                            error={form.name.error}
                            value={form.name.value}
                            onChange={handleChange}
                            className={`w-full`}
                        />
                        <Input
                            variant="secondary"
                            type="number"
                            name="mobile"
                            label="Mobile"
                            placeholder="Enter your mobile number"
                            maxLength={10}
                            error={form.mobile.error}
                            value={form.mobile.value}
                            onChange={handleChange}
                            className={`w-full`}
                        />
                        <Select
                            label="Station"
                            placeholder="Select Station"
                            variant="secondary"
                            value="id"
                            state="name"
                            options={stations}
                            selectedOption={form.station.value}
                            error={form.station.error}
                            onSelect={station => handleChange({ name: 'station', id: station.id, value: station.name })}
                        />
                        <div className="relative">
                            <TextArea
                                className="mb-[20px]"
                                variant="secondary"
                                name="reason"
                                label="Reason"
                                placeholder="Enter your reason"
                                rows={3}
                                maxLength={250}
                                error={form.reason.error}
                                value={form.reason.value}
                                onChange={handleChange}
                            />
                            <span className="absolute bottom-0 right-[10px] text-gray-400 text-xs font-normal">
                                {form.reason.value.length} / 250
                            </span>
                        </div>
                    </div>
                    <Button type="submit" variant="primary">Submit</Button>
                    </form>
                </div>
            )}
        </Modal>
    )
}